import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export default function Faqs() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/faqs?search=${encodeURIComponent(search)}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => {
    try {
      if (edit.id) await api.put(`/faqs/${edit.id}`, edit); else await api.post("/faqs", edit);
      toast.success("FAQ saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this FAQ?")) return; await api.delete(`/faqs/${id}`); toast.success("FAQ deleted"); load(); };
  return (
    <div>
      <PageHeader title="FAQs" subtitle="Answers counselors can use while talking to students" testid="faqs-header">
        {isAdmin && <Button onClick={() => setEdit({ question: "", answer: "" })} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="faq-add"><Plus className="w-4 h-4 mr-1" /> Add FAQ</Button>}
      </PageHeader>
      <div className="relative max-w-sm mb-4"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search FAQs (Enter)" className="pl-9 bg-white" data-testid="faq-search" /></div>
      {rows === null ? <Loading /> : (
        <Accordion type="multiple" className="bg-white border border-slate-200 rounded-xl px-4" data-testid="faq-list">
          {rows.map((f) => (
            <AccordionItem key={f.id} value={f.id}>
              <AccordionTrigger className="text-left font-semibold text-slate-800" data-testid={`faq-q-${f.id}`}>{f.question}</AccordionTrigger>
              <AccordionContent>
                <div className="text-sm text-slate-600 whitespace-pre-line">{f.answer}</div>
                {isAdmin && <div className="flex gap-2 mt-2"><Button size="sm" variant="outline" onClick={() => setEdit({ ...f })} data-testid={`faq-edit-${f.id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button><Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(f.id)} data-testid={`faq-del-${f.id}`}><Trash2 className="w-3 h-3 mr-1" /> Delete</Button></div>}
              </AccordionContent>
            </AccordionItem>
          ))}
          {!rows.length && <div className="py-8 text-center text-sm text-slate-400">No FAQs</div>}
        </Accordion>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit FAQ" : "Add FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Question</Label><Input value={edit?.question || ""} onChange={(e) => setEdit({ ...edit, question: e.target.value })} data-testid="faq-question" /></div>
            <div><Label>Answer</Label><Textarea rows={6} value={edit?.answer || ""} onChange={(e) => setEdit({ ...edit, answer: e.target.value })} data-testid="faq-answer" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="faq-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
